
import React, { useState, useEffect } from 'react';
import { DollarSign } from 'lucide-react';
import {
    getCostingStatus,
    calculateCosting,
    approveCosting
} from '../../../usecases/accounting-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import ButtonBasic from '../../atoms/buttonBasic/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import { Form, Table } from 'react-bootstrap';
import './style.css';
import ModalBtn from '../../molecules/modalButton/view';
import SubaccountSearchDropdown from '../../molecules/SubaccountSearchDropdown/view';

import { FiCheckCircle, FiAlertTriangle, FiDollarSign, FiInfo } from 'react-icons/fi';
import Tooltip from '@mui/material/Tooltip';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';

const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
];

const CostingModal = ({ id = "costing-modal" }) => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1); // Default current month
    const [costAccount, setCostAccount] = useState(null);
    const [inventoryAccount, setInventoryAccount] = useState(null);
    const [statusData, setStatusData] = useState(null);

    useEffect(() => {
        fetchStatus();
    }, [year, month]);

    const fetchStatus = async () => {
        try {
            const data = await getCostingStatus(year, month);
            setStatusData(data);
            if (data) {
                if (data.cost_account_id) setCostAccount({ id: data.cost_account_id });
                if (data.inventory_account_id) setInventoryAccount({ id: data.inventory_account_id });
            } else {
                // Keep accounts if previously selected or clear? Maybe keep them relative to user preference?
                // For now, reset if no data found to force selection or keep previous states if user is changing months?
                // Let's reset statusData, and if returned specific accounts use them. 
                // If not, we might want to keep selection if user is just switching months.
                // But simplified logic:
                setStatusData(null);
                setCostAccount(null);
                setInventoryAccount(null);
            }
        } catch (error) {
            console.error("Error fetching costing status", error);
        }
    };

    const handleCalculate = async () => {
        if (!costAccount?.id || !inventoryAccount?.id) {
            dispatchCustomEvent('popup', { type: 'error', message: "Debe seleccionar las cuentas de costo e inventario." });
            dispatchCustomEvent('loading-btn-calculate-cost', {});
            return;
        }
        try {
            const result = await calculateCosting({
                year: parseInt(year),
                month: parseInt(month),
                cost_account_id: parseInt(costAccount.id),
                inventory_account_id: parseInt(inventoryAccount.id)
            });
            setStatusData(result);
            dispatchCustomEvent('popup', { type: 'success', message: 'Costeo calculado correctamente (Borrador)' });
        } catch (error) {
            dispatchCustomEvent('popup', { type: 'error', message: 'Error al calcular costeo: ' + (error.message || 'Error desconocido') });
        } finally {
            dispatchCustomEvent('loading-btn-calculate-cost', {});
        }
    };

    const handleApprove = () => {
        dispatchCustomEvent('notification-confirm-approve-costing', { data: { year, month } });
    };

    const executeApproval = async () => {
        try {
            const result = await approveCosting({ year: parseInt(year), month: parseInt(month) });
            setStatusData(result);
            dispatchCustomEvent('popup', { type: 'success', message: 'Costeo APROBADO exitosamente' });
            dispatchCustomEvent('reload-comprobantes', {});
        } catch (error) {
            dispatchCustomEvent('popup', { type: 'error', message: 'Error al aprobar: ' + error.message });
        } finally {
            dispatchCustomEvent('loading-btn-approve-cost', {});
        }
    };

    return (
        <ModalBtn
            id='costing-modal'
            title="Costeo de Inventario Mensual"
            icon={<DollarSign size={20} />}
            style={{ width: '65vw', maxWidth: '95vw' }}
        >
            <div className="new-costing-container">
                {/* Left Column (30%) */}
                <div className="new-costing-sidebar">
                    <h5 className="new-costing-section-title">Costeo de Inventario Mensual</h5>
                    <p className="new-costing-section-desc">
                        Calcula y aprueba el costo de ventas de los productos comercializados en el mes. Este proceso generará el asiento contable correspondiente afectando las cuentas de costo e inventario.
                    </p>
                </div>

                {/* Right Column (70%) */}
                <div className="new-costing-form-right">
                    <div className="new-costing-fields-row" style={{ display: 'flex', gap: '20px', width: '100%' }}>
                        <Form.Group style={{ flex: 1 }}>
                            <label className="costing-label">Año Fiscal</label>
                            <Form.Select
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                            >
                                {[...Array(5)].map((_, i) => {
                                    const y = new Date().getFullYear() - i + 1;
                                    return <option key={y} value={y}>{y}</option>
                                })}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group style={{ flex: 1 }}>
                            <label className="costing-label">Mes</label>
                            <Form.Select
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                            >
                                {months.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </div>

                    <hr className="new-costing-separator" />

                    <div className="new-costing-fields-row" style={{ display: 'flex', gap: '20px', width: '100%' }}>
                        <Form.Group style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="costing-label mb-0">Cuenta Costo de Ventas</label>
                                <Tooltip title="Selecciona la cuenta contable de costo (Clase 6 o 7) para registrar la salida del costo de ventas." arrow placement="top">
                                    <span>
                                        <FiInfo className="text-muted cursor-pointer" size={14} />
                                    </span>
                                </Tooltip>
                            </div>
                            <SubaccountSearchDropdown
                                onSelected={(e) => setCostAccount(e)}
                                selected={costAccount}
                            />
                        </Form.Group>
                        <Form.Group style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="costing-label mb-0">Cuenta Inventario</label>
                                <Tooltip title="Selecciona la cuenta de activos/inventario (Clase 1, ej. 14) para descontar el valor de las mercancías vendidas." arrow placement="top">
                                    <span>
                                        <FiInfo className="text-muted cursor-pointer" size={14} />
                                    </span>
                                </Tooltip>
                            </div>
                            <SubaccountSearchDropdown
                                onSelected={(e) => setInventoryAccount(e)}
                                selected={inventoryAccount}
                            />
                        </Form.Group>
                    </div>

                    {statusData && (
                        <>
                            <hr className="new-costing-separator" />
                            <div className={`status-alert ${statusData.status === 'APPROVED' ? 'approved' : 'pending'}`}>
                                {statusData.status === 'APPROVED' ? (
                                    <div className="d-flex align-items-center">
                                        <FiCheckCircle size={18} className="me-2" />
                                        <strong>Costeo Aprobado - Total: ${parseFloat(statusData.total_cost).toLocaleString()}</strong>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center">
                                        <FiAlertTriangle size={18} className="me-2" />
                                        <div>
                                            <strong>Borrador Pendiente</strong> - Total Calculado: ${parseFloat(statusData.total_cost).toLocaleString()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {statusData?.details?.length > 0 && (
                        <div className="table-responsive" style={{ maxHeight: '250px' }}>
                            <Table size="sm" striped bordered hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Producto</th>
                                        <th className="text-end">Cant. Vendida</th>
                                        <th className="text-end">Costo Unit.</th>
                                        <th className="text-end">Total Costo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statusData.details.map((detail) => (
                                        <tr key={detail.id}>
                                            <td>{detail.product_name} ({detail.sku})</td>
                                            <td className="text-end">{parseFloat(detail.quantity_sold).toFixed(2)}</td>
                                            <td className="text-end">${parseFloat(detail.unit_cost).toFixed(2)}</td>
                                            <td className="text-end">${parseFloat(detail.total_cost).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    <div className="costing-footer">
                        <ButtonBasic
                            title="Cerrar"
                            type="option"
                            onClick={() => dispatchCustomEvent('close-modal', {})}
                        />

                        {(!statusData || statusData.status === 'PENDING') && (
                            <ButtonPrimary
                                id="calculate-cost"
                                title={statusData ? "Recalcular" : "Calcular Costeo"}
                                onClick={handleCalculate}
                            />
                        )}

                        {statusData?.status === 'PENDING' && (
                            <ButtonPrimary
                                id="approve-cost"
                                title="Aprobar Costeo"
                                onClick={handleApprove}
                                style={{ backgroundColor: 'var(--color-success)', color: 'white', border: 'none' }}
                            />
                        )}
                    </div>
                </div>
            </div>
            <ConfirmPopUp
                eventKey="approve-costing"
                msg="¿Está seguro de que desea aprobar el costeo? Se generará el asiento contable correspondiente."
                onAcept={executeApproval}
            />
        </ModalBtn>
    );
};


export default CostingModal;
